import * as R from 'ramda';
import React from 'react';
import {withRouter} from 'react-router-dom';
import {compose} from 'recompose';

import {translate} from 'iss/strings';
import {getEvent, getPreviousAnswers, reviewEvent} from 'iss/service/EvaluationEventService';
import {getForm} from 'iss/service/FormService';
import styles from 'iss/styles/FillEvaluationEditor.styl';
import dates from 'iss/utils/dates';
import subscribeTo from 'iss/utils/subscriptionUtils';
import {withLocalStorage} from 'iss/utils/withLocalStorage';
import {Button, CancelButton, SubmitButton} from 'iss/widgets/Buttons';
import {Form, FormSection, Input} from 'iss/widgets/Forms';
import {ErrorBox, RestResultMessage} from 'iss/widgets/MessageBox';
import {AnswerRadioSelector} from 'iss/widgets/Selectors';

const ReviewEvaluationPage = compose(
  withRouter,
  subscribeTo(({eventId}) => getEvent(eventId), "event"),
  subscribeTo(({eventId}) => getPreviousAnswers(eventId), "previousAnswers"),
  subscribeTo(({formId}) => getForm(formId), "form"),
  withLocalStorage(
    ({eventId, previousAnswers})=> 'eventAnswerStorage-1.1-'+eventId+'-'+previousAnswers.batchNumber,
    'answers', 'setAnswers', 'clearStoredAnswers',
    ({form, previousAnswers}) => {
      return {
        headings: getReviewableData(form, previousAnswers),
        eventInfo: R.pick(
          ['additionalEventInfo', 'eventInfoClarification', 'contactPerson'],
          previousAnswers
        )
      };
    }
))(class ReviewEvaluationPage extends React.Component {
  state = {
    backendError: null,
    showEventInfo: true
  };

  handleKeyDown = (e) => {
    const {key} = e;
    if (key == 'Enter') {
      e.preventDefault();
    }
  };

  reviewEvent = ({eventInfo}) => {
    reviewEvent(this.props.event.id, eventInfo).subscribe(
      () => {
        this.props.history.push('/event/'+this.props.event.id);
      },
      (errorResponse) => this.setState({backendError: errorResponse})
    );
  };

  render() {
    const {form, event, answers, setAnswers} = this.props;
    return <Form values={answers} valueUpdater={setAnswers} onSubmit={this.reviewEvent} onKeyDown={this.handleKeyDown}>
      {() =>
        <React.Fragment>
          <h1>{form.name}</h1>
          {this.state.showEventInfo ?
            <GeneralEventInfo event={event} aimedGradeText={form.aimedGrade ? form.aimedGrade.text : '-'} />
            :
            answers.headings.map((heading, headingNr) => {
                return <FormSection valuePath={['headings', headingNr]} key={heading.id}>
                  <Questions
                    heading={heading}
                    gradingScale={form.gradingScale}
                    remarksFieldNames={form.remarksFieldNames}
                  />
                </FormSection>;
              })
          }
          <RestResultMessage id="backendError" result={this.state.backendError} />
          <hr />
          <div className="form-group">
            {
              this.state.showEventInfo ?
              <Button id="start-review" buttonLook="primary" onClick={() => this.setState({showEventInfo: false})}>
                {translate('reviewEvaluationPage.reviewEvaluation')}
              </Button>
              :
              <SubmitButton id="submit-review">
                {translate('reviewEvaluationPage.reviewDone')}
              </SubmitButton>
            }
            <CancelButton link={'/activatedEvaluations/'+this.props.event.activationId} />
          </div>
        </React.Fragment>
      }
    </Form>;
  }
});

function GeneralEventInfo({event, aimedGradeText}) {
    return <React.Fragment>
      <ErrorBox id="stateError" message={
          (event.state === 'READY_FOR_REVIEW') ?
            null :
            translate('reviewEvaluationPage.stateError')
        } />
      <strong>{translate('fillEvaluationEditor.address')}</strong>
      <p id="event-info-street-address">{event.site.streetAddress}</p>
      <strong>{translate('fillEvaluationEditor.plannedDate')}</strong>
      <p id="event-info-planned-date">{event.plannedDate}</p>
      <strong>{translate('fillEvaluationEditor.currentDate')}</strong>
      <p id="event-info-current-date">{dates.formatUTC(new Date())}</p>
      <strong>{translate('fillEvaluationEditor.aimedGrade')}</strong>
      <p id="event-info-aimed-grade">{aimedGradeText}</p>

      <Input label="fillEvaluationEditor.contactPerson" id="event-info-contact-person" valuePath="eventInfo.contactPerson" maxLength="255" showCounter={true}/>
      <Input label="fillEvaluationEditor.eventInfoClarification" id="event-info-clarification" valuePath="eventInfo.eventInfoClarification" maxLength="500" showCounter={true}/>
      <Input label="fillEvaluationEditor.additionalEventInfo" id="event-info-additional" valuePath="eventInfo.additionalEventInfo" maxLength="500" showCounter={true}/>
    </React.Fragment>;
}

function Questions({heading, gradingScale, remarksFieldNames}) {
  return <div id="questions">
      <h2>{heading.title}</h2>
      {heading.questions.map((question, questionNr) =>
        <FormSection valuePath={["questions", questionNr]} key={questionNr}>
          <Question question={question} gradingScale={gradingScale} remarksFieldNames={remarksFieldNames} />
        </FormSection>
      )}
    </div>;
}

function Question({question, gradingScale, remarksFieldNames}) {
  const [remarks1Label, remarks2Label] = remarksFieldNames;
  return <React.Fragment>
    <h3 id={"question"+question.id} data-question-id={question.id}>{question.name}</h3>
    <p className="text-muted">{question.description}</p>
    <AnswerRadioSelector id={"answer-"+question.id} className={styles.answerOptions}
      valuePath={["answer", "gradeId"]} grades={gradingScale.grades} disabled />
    <AnswerField id={"remarks1-"+question.id} label={remarks1Label} value={question.answer.remarks1}/>
    <AnswerField id={"remarks2-"+question.id} label={remarks2Label} value={question.answer.remarks2}/>
  </React.Fragment>;
}

function AnswerField({id, label, value}) {
  return <div className="form-group">
    <label className="control-label" htmlFor={id}>{label}</label>
    <input id={id} className="form-control" value={value || ""} readOnly disabled />
  </div>;
}

function getReviewableData(form, previousAnswers) {
  const headingsWithAnswers = form.headings
  .map(heading => {
    const questions = heading.questions
            .map(question => {
              const answer = R.defaultTo({}, R.find(answer => answer.questionId === question.id, previousAnswers.answers));
              return {...question, answer: answer};
            });
    return {...heading, questions: questions};
  });
  const headingsWithQuestionsToShow = headingsWithAnswers
    .map(heading => ({...heading, questions: heading.questions.filter(question => isQuestionToBeReviewed(form, question))}))
    .filter(heading => heading.questions.length > 0);

    return headingsWithQuestionsToShow;
}

function isQuestionToBeReviewed(form, question) {
  return question.answer.gradeId !== form.aimedGrade.id
          || (!R.isNil(question.answer.remarks2) && question.answer.remarks2 !== '');
}

export default ReviewEvaluationPage;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/ReviewEvaluationPage.jsx